import { ArrowForwardIos } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import React, { useRef } from 'react'

/**
 * How long after a touch toggle a `click` is taken to be the same tap.
 */
const TOUCH_CLICK_WINDOW_MS = 1000

/**
 * How far (px) outside the button a touch may be lifted and still count as a tap on it.
 */
const TOUCH_SLOP_PX = 16

export interface ExpanderButtonProps {
  open: boolean
  disabled?: boolean
  color: string
  onToggle: () => void
}

/**
 * The chevron that opens and closes the details of a dashboard widget row.
 *
 * On a touch screen the toggle happens on `pointerup` instead of waiting for `click`.
 * iPhone WebKit (Safari and every other iOS browser) does not always turn a tap into
 * a `click`: the touch reaches the button (the ripple starts) but the synthetic click
 * never comes, and the user has to tap again. The home page re-renders on every
 * websocket update, and WebKit's tap heuristics are sensitive to the page changing
 * under the finger. Pointer events are dispatched straight from the touch, so they
 * arrive either way. When the `click` does follow, it is recognised as the same tap
 * and ignored. Mouse and keyboard keep using `click`.
 */
export function ExpanderButton(props: ExpanderButtonProps): React.ReactElement {
  const { open, disabled, color, onToggle } = props
  const touchToggledAt = useRef(0)

  const onPointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType !== 'touch' || disabled) {
      return
    }
    // A touch that ends up as a scroll is cancelled (`pointercancel`), but a finger
    // that slides off the button before lifting is not; don't count that as a tap.
    const rect = event.currentTarget.getBoundingClientRect()
    if (event.clientX < rect.left - TOUCH_SLOP_PX || event.clientX > rect.right + TOUCH_SLOP_PX ||
        event.clientY < rect.top - TOUCH_SLOP_PX || event.clientY > rect.bottom + TOUCH_SLOP_PX) {
      return
    }
    touchToggledAt.current = Date.now()
    onToggle()
  }

  const onClick = () => {
    if (Date.now() - touchToggledAt.current < TOUCH_CLICK_WINDOW_MS) {
      touchToggledAt.current = 0
      return
    }
    onToggle()
  }

  return (
    <IconButton disabled={disabled} onPointerUp={onPointerUp} onClick={onClick}>
      <ArrowForwardIos
        className={open ? 'opener-opened' : 'opener-closed'}
        sx={{ cursor: 'pointer', color: disabled ? 'gray' : color }}
      />
    </IconButton>
  )
}
